import React from 'react';
import { Helmet } from 'react-helmet-async';
import { alerts, news } from '../data/mockData';
import AlertBanner from '../components/AlertBanner';
import NewsCard from '../components/NewsCard';
import { FiArrowRight, FiActivity, FiShield, FiTrendingUp } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const Home = () => {
  const urgentAlert = alerts.find(a => a.type === 'URGENT RECALL');

  return (
    <>
      <Helmet>
        <title>Moringa Alert | Botanical Safety Journal & Recall Database</title>
      </Helmet>
      
      <AlertBanner alert={urgentAlert} />

      {/* Modern Hero / Journal Layout */}
      <section className="bg-surface py-12 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:grid lg:grid-cols-12 gap-12">
            
            {/* Main Feature */}
            <div className="lg:col-span-8">
              <div className="mb-6 flex items-center gap-3">
                <span className="bg-primary-dark text-white text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1">Investigative Report</span>
                <span className="text-gray-400 text-sm font-medium">Updated 2 hours ago</span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-serif font-black text-gray-900 mb-8 leading-[1.1]">
                Extensively Drug-Resistant Salmonella Outbreak Linked to Imported Moringa Batches
              </h1>
              
              <div className="relative aspect-[16/9] rounded-sm overflow-hidden mb-8 shadow-journal">
                <img 
                  src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?q=80&w=2000&auto=format&fit=crop" 
                  alt="Moringa Powder Lab Analysis" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              <p className="text-xl text-gray-600 leading-relaxed mb-8 font-medium">
                Federal investigators have traced the source of a recent multistate outbreak to specific processing facilities. New evidence suggests a breach in cold-chain protocols during international transit.
              </p>
              
              <Link to="/news/news-001" className="inline-flex items-center gap-3 text-primary font-black uppercase tracking-widest text-sm hover:gap-5 transition-all">
                Continue Reading <FiArrowRight />
              </Link>
            </div>

            {/* Sidebar Feed */}
            <div className="lg:col-span-4 space-y-10">
              <div className="border-l-4 border-primary pl-6 py-2">
                <h2 className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 mb-6 flex items-center gap-2">
                  <FiTrendingUp className="text-primary-light" /> Critical Safety Feed
                </h2>
                <div className="space-y-8">
                  {alerts.map(alert => (
                    <div key={alert.id} className="group cursor-pointer">
                      <span className="text-[10px] font-black text-alert uppercase tracking-widest block mb-2">{alert.type}</span>
                      <h3 className="font-serif font-bold text-gray-900 text-xl group-hover:text-primary transition-colors">
                        <Link to="/alerts">{alert.title}</Link>
                      </h3>
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{alert.summary}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-primary-dark text-white p-8 rounded-sm shadow-2xl">
                <FiShield className="text-primary-light text-4xl mb-6" />
                <h3 className="text-2xl font-serif font-bold mb-4">Verification Registry</h3>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                  Cross-reference your supplement batch numbers against our verified safety database.
                </p>
                <Link to="/products" className="block w-full text-center bg-white text-primary-dark font-black uppercase tracking-widest text-xs py-4 hover:bg-primary-light hover:text-white transition-all">
                  Access Database
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Grid Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-16 border-b border-gray-100 pb-8">
            <div>
              <h2 className="text-xs font-black uppercase tracking-[0.4em] text-primary mb-4">The Journal</h2>
              <p className="text-4xl font-serif font-black text-gray-900">Latest Dispatches</p>
            </div>
            <Link to="/news" className="hidden sm:flex items-center gap-2 text-sm font-black uppercase tracking-widest text-gray-400 hover:text-primary transition-all">
              View Archive <FiArrowRight />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {news.map(item => (
              <NewsCard key={item.id} news={item} />
            ))}
          </div>
        </div>
      </section>

      {/* Action Block */}
      <section className="bg-surface py-24 border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="inline-block bg-primary-light/10 text-primary font-black text-[10px] uppercase tracking-[0.4em] px-6 py-2 rounded-full mb-8">
            Public Safety Initiative
          </div>
          <h2 className="text-4xl lg:text-6xl font-serif font-black text-gray-900 mb-8">
            Stay Vigilant. <br/>Stay Informed.
          </h2>
          <p className="text-xl text-gray-500 mb-12 leading-relaxed">
            Moringa Alert is dedicated to providing transparent, science-backed reporting on botanical purity. Join 40,000+ consumers receiving weekly safety audits.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <input 
              type="email" 
              placeholder="Enter your email address" 
              className="px-8 py-4 bg-white border border-gray-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary w-full sm:w-80 shadow-sm"
            />
            <button className="bg-primary-dark text-white px-10 py-4 font-black uppercase tracking-widest text-xs hover:bg-primary transition-all shadow-journal">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;